import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './PenaltyPage.css';

import izquierdaArq from '../../img/izquierdaArq.png';
import derechaArq from '../../img/derechaArq.png';
import centroArq from '../../img/centroArq.png';

const ALL_ZONES = [
    { id: 'TL', label: 'Top Left' },
    { id: 'TM', label: 'Top Middle' },
    { id: 'TR', label: 'Top Right' },
    { id: 'MN', label: 'Middle Normal' },
    { id: 'MC', label: 'Middle Chip' },
    { id: 'BL', label: 'Bottom Left' },
    { id: 'BR', label: 'Bottom Right' },
];

const ZONE_POSITIONS = {
    TL: { left: '20%', top: '15%' },
    TM: { left: '50%', top: '12%' },
    TR: { left: '80%', top: '15%' },
    MN: { left: '50%', top: '50%' },
    MC: { left: '50%', top: '50%' },
    BL: { left: '20%', top: '85%' },
    BR: { left: '80%', top: '85%' },
};

function getRandomSubset(arr, n) {
    const copy = [...arr];
    const result = [];
    while (result.length < n && copy.length) {
        const idx = Math.floor(Math.random() * copy.length);
        result.push(copy.splice(idx, 1)[0]);
    }
    return result;
}

function PrecisionBar({ speed, onChange }) {
    const [pos, setPos] = useState(0);
    const [dir, setDir] = useState(1);

    useEffect(() => {
        const iv = setInterval(() => {
            setPos(p => {
                let next = p + dir * (100 / (speed / 20));
                if (next >= 100) { next = 100; setDir(-1); }
                if (next <= 0) { next = 0; setDir(1); }
                return next;
            });
        }, 20);
        return () => clearInterval(iv);
    }, [speed, dir]);

    useEffect(() => onChange(pos), [pos, onChange]);

    return (
        <div className="precision-bar">
            <div className="precision-indicator" style={{ left: `${pos}%` }} />
        </div>
    );
}

const PenaltyPage = () => {
    const { combinationWinner, startingAction } = useLocation().state || {};
    const [turn, setTurn] = useState(startingAction || 'userShoot');
    const [scoreUser, setScoreUser] = useState(0);
    const [scoreBot, setScoreBot] = useState(0);
    const [precisionPos, setPrecisionPos] = useState(0);
    const [finished, setFinished] = useState(false);

    const [animating, setAnimating] = useState(false);
    const [ballStyle, setBallStyle] = useState({});
    const [diveZone, setDiveZone] = useState(null);
    const [shotZone, setShotZone] = useState(null);

    const isUserShooting = turn === 'userShoot';
    const userWonCombo = combinationWinner === 'user';

    // opciones según ventaja
    const options = isUserShooting
        ? (userWonCombo ? ALL_ZONES : getRandomSubset(ALL_ZONES, 3))
        : (!userWonCombo ? ALL_ZONES : getRandomSubset(ALL_ZONES, 3));

    const speed = (isUserShooting && !userWonCombo) ? 1000 : 2000;

    // limpia zonas cuando cambia turno
    useEffect(() => {
        setDiveZone(null);
        setShotZone(null);
    }, [turn]);

    // preselecciona tiro del bot de las mismas opciones
    useEffect(() => {
        if (turn === 'botShoot') {
            if (Math.random() < 0.1) {
                setShotZone({ id: 'OUT', label: 'Out' });
            } else {
                const pool = options;
                setShotZone(pool[Math.floor(Math.random() * pool.length)]);
            }
        }
    }, [turn, options]);

    // elige imagen de arquero según diveZone
    const getGoalieImage = zone => {
        if (!zone) return centroArq;
        if (zone.id === 'TL' || zone.id === 'BL') return izquierdaArq;
        if (zone.id === 'TR' || zone.id === 'BR') return derechaArq;
        return centroArq;
    };

    const handleZoneSelect = zone => {
        if (animating) return;
        let userDive, userShot;

        if (isUserShooting) {
            userShot = zone;
            const divePool = userWonCombo ? ALL_ZONES : getRandomSubset(ALL_ZONES, 3);
            userDive = divePool[Math.floor(Math.random() * divePool.length)];
        } else {
            userDive = zone;
            userShot = shotZone;
        }

        setDiveZone(userDive);
        setShotZone(userShot);

        // detectar falla
        const blocked = isUserShooting
            ? (userShot.id === userDive.id || precisionPos < 25 || precisionPos > 75)
            : false;
        const missed = isUserShooting ? blocked : (shotZone.id === 'OUT');

        // calcula coords
        let targetCoords;
        if (missed) {
            if (isUserShooting) {
                const base = ZONE_POSITIONS[userShot.id];
                if (userShot.id === 'TL' || userShot.id === 'BL') {
                    targetCoords = { left: '-10%', top: base.top };
                } else if (userShot.id === 'TR' || userShot.id === 'BR') {
                    targetCoords = { left: '110%', top: base.top };
                } else {
                    targetCoords = { left: base.left, top: '-20%' };
                }
            } else {
                targetCoords = { left: '50%', top: '-20%' };
            }
        } else {
            const destId = isUserShooting ? userShot.id : shotZone.id;
            targetCoords = ZONE_POSITIONS[destId];
        }

        // anima balón
        const start = isUserShooting
            ? { left: '50%', top: '100%' }
            : { left: '50%', top: '-10%' };
        setBallStyle({ ...start, transition: 'none' });
        setAnimating(true);

        requestAnimationFrame(() => {
            setBallStyle({ ...targetCoords, transition: 'all 0.8s ease-out' });
        });

        setTimeout(() => {
            if (isUserShooting) {
                if (!missed) setScoreUser(s => s + 1);
                setTurn('botShoot');
            } else {
                const isSave = (userDive.id === shotZone.id);
                if (!isSave) setScoreBot(s => s + 1);
                setFinished(true);
            }
            setAnimating(false);
        }, 900);
    };

    if (finished) {
        return (
            <div className="penalty-page">
                <div className="penalty-container">
                    <h2>🏁 Shootout Result</h2>
                    <p className="result">
                        You {scoreUser > scoreBot
                            ? 'win!'
                            : (scoreUser < scoreBot ? 'lose.' : 'draw.')}
                    </p>
                    <p>Final Score: You {scoreUser} - {scoreBot} Bot</p>
                </div>
            </div>
        );
    }

    return (
        <div className="penalty-page">
            <div className="penalty-container">
                <h2>⚽ Penalty Shootout</h2>
                <p className="turn-indicator">
                    {isUserShooting ? 'Your turn to shoot' : 'Your turn to save'}
                </p>

                <div className="goal-container">
                    <div className="goal-net" />
                    <div className="goal-frame" />

                    {/* arquero real */}
                    <img
                        src={getGoalieImage(diveZone)}
                        alt="Goalkeeper"
                        className="goalie-img"
                        style={{
                            left: diveZone ? ZONE_POSITIONS[diveZone.id].left : '50%',
                            top: diveZone ? ZONE_POSITIONS[diveZone.id].top : '50%',
                        }}
                    />

                    {/* balón en vuelo */}
                    {animating && (
                        <div className="ball" style={ballStyle} />
                    )}

                    {/* balón en guante si ataja */}
                    {!animating && diveZone && shotZone?.id === diveZone.id && (
                        <div
                            className="ball-catching"
                            style={{
                                left: ZONE_POSITIONS[diveZone.id].left,
                                top: ZONE_POSITIONS[diveZone.id].top,
                            }}
                        />
                    )}
                </div>

                {isUserShooting && (
                    <PrecisionBar speed={speed} onChange={setPrecisionPos} />
                )}

                <div className="zones-container">
                    {options.map(z => (
                        <button
                            key={z.id}
                            className="zone-btn"
                            disabled={animating}
                            onClick={() => handleZoneSelect(z)}
                        >
                            {z.label}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PenaltyPage;
